import { environment } from '../environment/environment';

// ── Types ──────────────────────────────────────────────────────────────────

export interface RecommendedSolution {
  Solution: string;
  Reasoning: string;
}

export interface OpportunityStore {
  gk_name: string;
  atlas_id: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  RecommendedSolutions: RecommendedSolution[];
}

export interface CrmOpportunity {
  Topic: string;
  CurrentSituation: string;
  PotentialCustomer: string;
  CustomerNeed: string;
  CustomerPainPoints: string;
  EstCloseDate: string;
  EstRevenue: number;
  Probability: string;
  NoOfStores: number;
  OpportunitySiteType: string;
  PricePerStore: number;
  UltimateParent: string;
  Assignee?: string;
  ProposedSolutions: string[];
}

export interface CreateCrmOpportunityInput {
  Opportunity: CrmOpportunity;
  OpportunityStores: OpportunityStore[];
}

export interface CreateCrmOpportunityResult {
  success: boolean;
  message: string;
  data?: {
    RecordUrl: string;
    RecordId: string;
    Status: string;
    ErrorMessage: string | null;
    ErrorResponse: string | null;
  };
}

export interface CrmOpportunityStore {
  name: string;
  store: string | null;
  storeNumber: string;
  atlas_id: string;
  ProposedSolutions: string;
}

export interface CrmOpportunityRecord {
  OpportunityId: string;
  Topic: string;
  PotentialCustomer: string;
  PotentialCustomerId: string | null;
  EstRevenue: number;
  CreatedOn: string;
  EstimatedCloseDate: string | null;
  OpportunitySiteType: string | null;
  PricePerStore: number | null;
  OpportunityStores: CrmOpportunityStore[];
}

export interface PaginationInfo {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface GetCrmOpportunitiesResult {
  success: boolean;
  message: string;
  opportunities: CrmOpportunityRecord[];
  pagination: PaginationInfo;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const escapeGql = (str: string): string => {
  if (!str) return '';
  return str.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
};


const buildOpportunityGql = (opp: CrmOpportunity): string => {
  const lines = [
    `      Topic: "${escapeGql(opp.Topic)}"`,
    `      CurrentSituation: "${escapeGql(opp.CurrentSituation)}"`,
    `      PotentialCustomer: "${escapeGql(opp.PotentialCustomer)}"`,
    `      UltimateParent: "${escapeGql(opp.UltimateParent)}"`,
    `      CustomerNeed: "${escapeGql(opp.CustomerNeed)}"`,
    `      CustomerPainPoints: "${escapeGql(opp.CustomerPainPoints)}"`,
    `      EstCloseDate: "${escapeGql(opp.EstCloseDate)}"`,
    `      EstRevenue: ${opp.EstRevenue}`,
    `      Probability: "${escapeGql(opp.Probability)}"`,
    `      NoOfStores: ${opp.NoOfStores}`,
    `      OpportunitySiteType: "${escapeGql(opp.OpportunitySiteType)}"`,
    `      PricePerStore: ${opp.PricePerStore}`,
    `      ProposedSolutions: [${opp.ProposedSolutions.map(sol => `"${escapeGql(sol)}"`).join(', ')}]`,
  ];

  if (opp.Assignee) {
    lines.push(`      Assignee: "${escapeGql(opp.Assignee)}"`);
  }

  return `{\n${lines.join('\n')}\n    }`;
};

const buildStoresGql = (stores: OpportunityStore[]): string => {
  const items = stores.map(store => {
    const solutions = store.RecommendedSolutions.map(
      sol => `{ Solution: "${escapeGql(sol.Solution)}", Reasoning: "${escapeGql(sol.Reasoning)}" }`
    ).join(', ');
    return `{ gk_name: "${escapeGql(store.gk_name)}", atlas_id: "${escapeGql(store.atlas_id)}", addressLine1: "${escapeGql(store.addressLine1)}", addressLine2: "${escapeGql(store.addressLine2)}", city: "${escapeGql(store.city)}", state: "${escapeGql(store.state)}", country: "${escapeGql(store.country)}", postalCode: "${escapeGql(store.postalCode)}", RecommendedSolutions: [${solutions}] }`;
  });
  return `[${items.join(', ')}]`;
};

const buildMutation = (opp: CrmOpportunity, stores: OpportunityStore[]): string => {
  const query = `
  mutation {
    createCrmOpportunity(
      Opportunity: ${buildOpportunityGql(opp)}
      OpportunityStores: ${buildStoresGql(stores)}
    ) {
      RecordUrl
      RecordId
      Status
      ErrorMessage
      ErrorResponse
    }
  }
`;
  // Debug: Log the mutation query to inspect for issues
  console.log('[CRM Service] GraphQL Mutation:', query);
  return query;
};

const extractErrorMessage = (error: any): string | null =>
  error.response?.errors?.[0]?.message ||
  error.response?.errors?.[0]?.extensions?.internal?.response?.body?.message ||
  null;

// ── Service ────────────────────────────────────────────────────────────────

export const createCrmOpportunity = async (
  input: CreateCrmOpportunityInput
): Promise<CreateCrmOpportunityResult> => {
  try {
    const token = localStorage.getItem('access_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const query = buildMutation(input.Opportunity, input.OpportunityStores);
    const requestBody = JSON.stringify({ query });

    // Debug: Log the request body
    console.log('[CRM Service] Request Body:', requestBody);

    const response = await fetch(environment.graphqlEndpoint, {
      method: 'POST',
      headers,
      body: requestBody,
    });

    const result = await response.json();

    // Debug: Log the response
    console.log('[CRM Service] Response:', result);

    if (result.errors) {
      const error = new Error('CRM export failed');
      (error as any).response = result;
      throw error;
    }

    const { Status, ErrorMessage } = result.data.createCrmOpportunity;

    if (Status === 'Success') {
      return {
        success: true,
        message: 'Opportunity exported to CRM successfully!',
        data: result.data.createCrmOpportunity,
      };
    }

    return {
      success: false,
      message: ErrorMessage || 'CRM returned an error',
      data: result.data.createCrmOpportunity,
    };
  } catch (error: any) {
    console.error('createCrmOpportunity error:', error);

    return {
      success: false,
      message:
        extractErrorMessage(error) ||
        (error instanceof Error ? error.message : 'Failed to create CRM opportunity'),
    };
  }
};

// ── Get CRM Opportunities ──────────────────────────────────────────────────

export const getCrmOpportunities = async (
  from: string,
  to: string,
  page: number = 1,
  ultimateParent?: string
): Promise<GetCrmOpportunitiesResult> => {
  try {
    const token = localStorage.getItem('access_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const upParam = ultimateParent ? `, UltimateParent: "${escapeGql(ultimateParent)}"` : '';
    const query = `
      query {
        getCrmOpportunities(From: "${escapeGql(from)}", To: "${escapeGql(to)}", Top: ${page}${upParam}) {
          Status
          ErrorMessage
          Page
          Limit
          TotalCount
          TotalPages
          HasNextPage
          HasPreviousPage
          Opportunities {
            OpportunityId
            Topic
            PotentialCustomer
            PotentialCustomerId
            EstRevenue
            CreatedOn
            EstimatedCloseDate
            OpportunitySiteType
            PricePerStore
            OpportunityStores {
              name
              store
              storeNumber
              atlas_id
              ProposedSolutions
            }
          }
        }
      }
    `;

    const response = await fetch(environment.graphqlEndpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query }),
    });

    const result = await response.json();

    if (result.errors) {
      const error = new Error('Failed to fetch CRM opportunities');
      (error as any).response = result;
      throw error;
    }

    const data = result.data.getCrmOpportunities;

    const pagination: PaginationInfo = {
      page: data.Page || 1,
      limit: data.Limit || 50,
      totalCount: data.TotalCount || 0,
      totalPages: data.TotalPages || 1,
      hasNextPage: data.HasNextPage || false,
      hasPreviousPage: data.HasPreviousPage || false,
    };

    if (data.Status === 'Success') {
      return {
        success: true,
        message: 'Opportunities fetched successfully',
        opportunities: data.Opportunities || [],
        pagination,
      };
    }

    return {
      success: false,
      message: data.ErrorMessage || 'Failed to fetch opportunities',
      opportunities: [],
      pagination,
    };
  } catch (error: any) {
    console.error('getCrmOpportunities error:', error);

    return {
      success: false,
      message:
        extractErrorMessage(error) ||
        (error instanceof Error ? error.message : 'Failed to fetch CRM opportunities'),
      opportunities: [],
      pagination: { page: 1, limit: 50, totalCount: 0, totalPages: 1, hasNextPage: false, hasPreviousPage: false },
    };
  }
};
